import React, { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation, matchPath, Link,} from "react-router-dom";
import { useAuth } from "../../../auth/AuthContext";
import { useShortName } from "../../../utils/useShortName";
import { teacherGetRuns, getMessageUnreadCount } from "../../../api/academic";
import {
	getNotifications,
	markNotificationRead,
	markAllNotificationsRead,
} from "../../../api/notifications";
import {
	capitalizeWords,
	capitalizeFirstLetter,
} from "../../../utils/letters";
import GlobalChatDrawer from "../../course/GlobalChatDrawer";
import CourseMessagesDrawer from "../../course/CourseMessagesDrawer";
import PortalTopBar from "../shared/PortalTopBar";
import PortalSidebarShell from "../shared/PortalSidebarShell";
import ClassFooter from '../../student/classroom/classFooter';

/**
 * Misma estructura visual que el portal estudiante (student-portal.css):
 * student-portal-container → portal-sidebar + portal-main-content → div-portal-content.
 * Barra superior: mismos iconos de mensajes (badge) y notificaciones que el aula estudiante.
 */
export default function TeacherLayout({ footerData, isLoading: footerLoading }) {
	useEffect(() => {
		document.title = "Portal Docente | Somnus";
	}, []);
	const shortDisplayName = useShortName();
	const navigate = useNavigate();
	const location = useLocation();
	const { user, logout } = useAuth();
	const [runs, setRuns] = useState([]);
	const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
	const [desktopSidebarHidden, setDesktopSidebarHidden] = useState(false);

	const [chatDrawerOpen, setChatDrawerOpen] = useState(false);
	const [messageUnreadCount, setMessageUnreadCount] = useState(0);
	const [notifications, setNotifications] = useState([]);
	const [notifUnreadCount, setNotifUnreadCount] = useState(0);
	const [notifOpen, setNotifOpen] = useState(false);
	const [userMenuOpen, setUserMenuOpen] = useState(false);
	const userMenuRef = useRef(null);

	const courseMatch = matchPath(
		{ path: "/portal-doc/cursos/:runId/*" },
		location.pathname,
	);
	const activeRunId = courseMatch?.params?.runId;

	const loadMessageUnreadCount = useCallback(async () => {
		try {
			const count = await getMessageUnreadCount();
			setMessageUnreadCount(count);
		} catch {
			setMessageUnreadCount(0);
		}
	}, []);

	const loadNotifications = useCallback(async () => {
		try {
			const data = await getNotifications();
			setNotifications(data.results || []);
			setNotifUnreadCount(data.unread_count ?? 0);
		} catch {
			setNotifications([]);
			setNotifUnreadCount(0);
		}
	}, []);

	useEffect(() => {
		let cancelled = false;
		teacherGetRuns()
			.then((data) => {
				if (!cancelled) setRuns(Array.isArray(data) ? data : []);
			})
			.catch(() => { });
		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		loadMessageUnreadCount();
		const interval = setInterval(loadMessageUnreadCount, 15000);
		return () => clearInterval(interval);
	}, [loadMessageUnreadCount]);

	useEffect(() => {
		if (user) loadNotifications();
	}, [user, loadNotifications]);

	useEffect(() => {
		const interval = setInterval(loadNotifications, 15000);
		return () => clearInterval(interval);
	}, [loadNotifications]);

	useEffect(() => {
		setMobileSidebarOpen(false);
		setUserMenuOpen(false);
	}, [location.pathname]);

	useEffect(() => {
		if (!userMenuOpen) return;
		const handleClickOutside = (event) => {
			if (!userMenuRef.current?.contains(event.target)) {
				setUserMenuOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [userMenuOpen]);

	useEffect(() => {
		if (!mobileSidebarOpen) return;
		const prev = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = prev;
		};
	}, [mobileSidebarOpen]);

	useEffect(() => {
		const onKey = (e) => {
			if (e.key === "Escape") setMobileSidebarOpen(false);
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, []);

	const handleToggleNotif = () => {
		if (!notifOpen) loadNotifications();
		setNotifOpen((o) => !o);
	};

	const handleMarkRead = async (id) => {
		try {
			await markNotificationRead(id);
			setNotifications((prev) =>
				prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)),
			);
			setNotifUnreadCount((c) => Math.max(0, c - 1));
		} catch { }
	};

	const handleMarkAllRead = async () => {
		try {
			await markAllNotificationsRead();
			setNotifications((prev) =>
				prev.map((n) => ({ ...n, is_read: true })),
			);
			setNotifUnreadCount(0);
		} catch { }
	};

	const handleLogout = async () => {
		try {
			await logout();
		} finally {
			navigate("/login-doc", { replace: true });
		}
	};

	const toggleSidebar = () => {
		const isMobileViewport =
			typeof window !== "undefined" &&
			window.matchMedia("(max-width: 931px)").matches;

		if (isMobileViewport) {
			setMobileSidebarOpen((open) => !open);
			return;
		}

		setDesktopSidebarHidden((hidden) => !hidden);
	};

	const sidebarToggleLabel =
		mobileSidebarOpen || !desktopSidebarHidden
			? "Ocultar panel izquierdo"
			: "Mostrar panel izquierdo";
	const isSidebarVisible = mobileSidebarOpen || !desktopSidebarHidden;

	const teacherDisplayName = capitalizeWords(
		user?.fullName || shortDisplayName || "Docente",
	);
	const teacherInitial = teacherDisplayName.charAt(0).toUpperCase();

	const headerIconProps = {
		messageUnreadCount,
		notifUnreadCount,
		notifOpen,
		notifications,
		chatDrawerOpen,
		onOpenChat: () => setChatDrawerOpen(true),
		onToggleNotif: handleToggleNotif,
		onCloseNotifBackdrop: () => setNotifOpen(false),
		onMarkRead: handleMarkRead,
		onMarkAllRead: handleMarkAllRead,
	};

	const topBarProps = {
		sidebarToggleLabel,
		isSidebarVisible,
		onToggleSidebar: toggleSidebar,
		headerIconProps,
		userMenuOpen,
		userMenuRef,
		onToggleUserMenu: () => setUserMenuOpen((open) => !open),
		userAvatarColor: "var(--light-blue)",
		userInitial: teacherInitial,
		userDisplayName: teacherDisplayName,
		profileTo: "/portal-doc/perfil",
		onCloseUserMenu: () => setUserMenuOpen(false),
		onLogout: handleLogout,
		menuAriaLabel: "Opciones de cuenta docente",
	};

	return (
		<div className="student-portal-container teacher-portal-layout">
			<PortalSidebarShell
				portalSubtitle="Portal Docente"
				userLabel="Docente conectado"
				userName={teacherDisplayName}
				mobileSidebarOpen={mobileSidebarOpen}
				desktopSidebarHidden={desktopSidebarHidden}
				sidebarToggleLabel={sidebarToggleLabel}
				isSidebarVisible={isSidebarVisible}
				onToggleSidebar={toggleSidebar}
				onCloseSidebar={() => setMobileSidebarOpen(false)}
			>
				<div
					className="text-uppercase text-muted mt-1 mb-1"
					style={{ fontSize: 11 }}
				>
					Área personal
				</div>
				<NavLink
					to="/portal-doc"
					end
					className={({ isActive }) =>
						"nav-link custom-block-link d-flex align-items-stretch p-0 rounded-2 mb-2 " +
						(isActive ? "admin-active-link" : "dashboard")
					}
					onClick={() => setMobileSidebarOpen(false)}
				>
					<i className="ri-dashboard-fill icon-part" />
					<span className="text-part">Academia</span>
				</NavLink>

				<Link
                    to="/"
                    className="nav-link custom-block-link d-flex align-items-stretch p-0 rounded-2 mb-2 home"
                >
                    <i className="ri-home-2-fill icon-part" />
                    <span className="text-part">Inicio</span>
                </Link>

				<div
					className="text-uppercase text-muted mt-3 mb-1"
					style={{ fontSize: 11 }}
				>
					Mis cursos
				</div>
				{runs.length > 0 && (
					<>
						{runs.map((run) => (
							<NavLink
								key={run.id}
								to={`/portal-doc/cursos/${run.id}`}
								className={({ isActive }) =>
									"nav-link custom-block-link d-flex align-items-stretch p-0 rounded-2 mb-2 " +
									(isActive
										? "admin-active-link"
										: "course")
								}
								onClick={() => setMobileSidebarOpen(false)}
								title={
									capitalizeFirstLetter(run.name) ||
									run.code ||
									`Curso ${run.id}`
								}
							>
								{/* Añadimos flex-shrink-0 para proteger el icono */}
								<i className="ri-book-2-fill icon-part flex-shrink-0" />

								<span className="text-part text-truncate text-course-small">
									{capitalizeFirstLetter(run.name) ||
										run.code ||
										`Curso ${run.id}`}
								</span>
							</NavLink>
						))}
					</>
				)}

				<div
					className="text-uppercase text-muted mt-3 mb-1"
					style={{ fontSize: 11 }}
				>
					Ayuda
				</div>
				<NavLink
					to="/portal-doc/capacitacion"
					className={({ isActive }) =>
						"nav-link custom-block-link d-flex align-items-stretch p-0 rounded-2 mb-2 " +
						(isActive ? "admin-active-link" : "instr")
					}
					onClick={() => setMobileSidebarOpen(false)}
				>
					<i className="ri-book-open-fill icon-part" />
					<span className="text-part">Instructivo</span>
				</NavLink>
			</PortalSidebarShell>
			

			<main
				className="flex-grow-1 d-flex flex-column"
				style={{ minWidth: 0 }}
			>
				<PortalTopBar {...topBarProps} />
				<Outlet />
				<ClassFooter footerData={footerData} isLoading={footerLoading} />
			</main>
			{!activeRunId && (
				<GlobalChatDrawer
					open={chatDrawerOpen}
					onClose={() => setChatDrawerOpen(false)}
					courses={runs}
					onUnreadChange={loadMessageUnreadCount}
					emptyCoursesMessage="No tienes cursos asignados."
				/>
			)}

			{/* 2. Si ESTÁ dentro de un curso, abrimos el Drawer que pide el runId */}
			{activeRunId && (
				<CourseMessagesDrawer
					open={chatDrawerOpen}
					onClose={() => setChatDrawerOpen(false)}
					runId={activeRunId}
					courseName="Mensajes"
				/>
			)}
			
		</div>
	);
}
